import { useState, useEffect, useRef } from 'react';
import onError from '~/errorsHandler';

type FetchState<Data> = {
  data?: Data;
  /** A call is in flight, including a refetch that still has previous data on screen. */
  fetching: boolean;
  error?: any;
  response?: any;
};

type State<Data> = FetchState<Data> & {
  /** Nothing to render yet: a call is in flight and no data has arrived for it. */
  loading: boolean;
};

export type UseApiReturn<Data = any> = [State<Data>, () => void];

const useApi = <Data = any>(
  call: () => Promise<any>,
  deps: readonly any[] = []
): UseApiReturn<Data> => {
  const [state, setState] = useState<FetchState<Data>>({ fetching: true });
  /**
   * Number of the most recently started call. Nothing cancels an in-flight request,
   * so when a deps change or a refetch starts a second one, both responses arrive and
   * the slower one wins — even if it belongs to the older deps. Responses whose number
   * is no longer the latest are dropped instead of being written to the state.
   */
  const latestCall = useRef(0);

  const query = () => {
    const callId = ++latestCall.current;

    setState((current) => ({ ...current, fetching: true, error: undefined }));

    call()
      .then((response) => {
        if (callId !== latestCall.current) return;
        setState({
          data: response?.data,
          fetching: false,
          response,
        });
      })
      .catch((error) => {
        if (callId !== latestCall.current) return;
        setState({
          fetching: false,
          error,
        });
        onError(error);
      });
  };

  useEffect(() => {
    query();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  return [{ ...state, loading: state.fetching && state.data === undefined }, () => query()];
};

export default useApi;
